/**
 * Balanço Demanda × Capacidade do Painel.
 *
 * Convenção única: Gap = Capacidade − Demanda.
 *   positivo = folga de capacidade
 *   zero     = balanceado
 *   negativo = déficit
 *
 * Unidade: tarefas/h.
 * Capacidade = FTE disponível × tarefas por pessoa por hora.
 * FTE necessário = Demanda / produtividade.
 * Déficit de FTE = max(0, FTE necessário − FTE disponível).
 *
 * WIP e throughput não são demanda. O chamador só informa demanda
 * quando ela foi configurada ou digitada num cenário simulado.
 */

export type CapacityGapStatus = 'deficit' | 'balanced' | 'slack';

export const CAPACITY_GAP_STATUS_LABEL: Record<CapacityGapStatus, string> = {
  deficit: 'Déficit',
  balanced: 'Balanceado',
  slack: 'Folga',
};

export interface CapacityBalanceInput {
  /** Tarefas necessárias por hora. Não é quantidade de tarefas abertas. */
  demandTasksPerHour: number;
  /** Produtividade parametrizada. Não é o limite de tarefas ativas simultâneas. */
  tasksPerPersonHour: number;
  availableFte: number;
}

export interface CapacityBalance {
  capacityTasksPerHour: number;
  gapTasksPerHour: number;
  requiredFte: number;
  fteDeficit: number;
  gapStatus: CapacityGapStatus;
}

export type CapacityBlocker = 'demand' | 'productivity' | 'fte';

export interface CapacityOutlookInput {
  /** Null = demanda inexistente. Zero = demanda configurada igual a zero. */
  demandTasksPerHour: number | null;
  demandOrigin?: 'department' | 'organization' | null;
  /** Null = não há FTE formal. Contagem de pessoas não entra aqui. */
  availableFte: number | null;
  /** Null = produtividade não configurada. Não usar 1 como fallback. */
  tasksPerPersonHour: number | null;
  peopleInScope?: number | null;
}

export interface CapacityOutlook {
  demandTasksPerHour: number | null;
  demandOrigin: 'department' | 'organization' | null;
  capacityTasksPerHour: number | null;
  gapTasksPerHour: number | null;
  gapStatus: CapacityGapStatus | null;
  requiredFte: number | null;
  availableFte: number | null;
  fteDeficit: number | null;
  peopleInScope: number | null;
  tasksPerPersonHour: number | null;
  blockers: CapacityBlocker[];
}

/**
 * Capacidade horária só existe com FTE formal e produtividade informados.
 * Pessoas no escopo não substituem FTE.
 * Dado ausente permanece null. Zero só aparece quando o valor foi informado.
 */
export function buildCapacityOutlook(input: CapacityOutlookInput): CapacityOutlook {
  const demand = finiteOrNull(input.demandTasksPerHour);
  const demandValue = demand != null && demand >= 0 ? demand : null;
  const productivity = finiteOrNull(input.tasksPerPersonHour);
  const productivityValue = productivity != null && productivity > 0 ? productivity : null;
  const availableFte = finiteOrNull(input.availableFte);
  const fteValue = availableFte != null && availableFte >= 0 ? availableFte : null;
  const blockers: CapacityBlocker[] = [];
  if (demandValue == null) blockers.push('demand');
  if (productivityValue == null) blockers.push('productivity');
  if (fteValue == null) blockers.push('fte');

  const requiredFte = demandValue != null && productivityValue != null ? demandValue / productivityValue : null;
  const capacityTasksPerHour = productivityValue != null && fteValue != null ? fteValue * productivityValue : null;
  const gapTasksPerHour = demandValue != null && capacityTasksPerHour != null
    ? capacityTasksPerHour - demandValue
    : null;
  const gapStatus = gapTasksPerHour == null
    ? null
    : gapTasksPerHour < 0
      ? 'deficit'
      : gapTasksPerHour === 0
        ? 'balanced'
        : 'slack';
  const fteDeficit = requiredFte != null && fteValue != null ? Math.max(0, requiredFte - fteValue) : null;

  return {
    demandTasksPerHour: demandValue,
    demandOrigin: demandValue != null ? input.demandOrigin ?? null : null,
    capacityTasksPerHour,
    gapTasksPerHour,
    gapStatus,
    requiredFte,
    availableFte: fteValue,
    fteDeficit,
    peopleInScope: finiteOrNull(input.peopleInScope ?? null),
    tasksPerPersonHour: productivityValue,
    blockers,
  };
}

function finiteOrNull(value: number | null): number | null {
  return value != null && Number.isFinite(value) ? value : null;
}

export function capacityBalance(input: CapacityBalanceInput): CapacityBalance | null {
  const { demandTasksPerHour, tasksPerPersonHour, availableFte } = input;
  if (!Number.isFinite(demandTasksPerHour) || demandTasksPerHour < 0) return null;
  if (!Number.isFinite(tasksPerPersonHour) || tasksPerPersonHour <= 0) return null;
  if (!Number.isFinite(availableFte) || availableFte < 0) return null;

  const capacityTasksPerHour = availableFte * tasksPerPersonHour;
  const gapTasksPerHour = capacityTasksPerHour - demandTasksPerHour;
  const requiredFte = demandTasksPerHour / tasksPerPersonHour;
  const gapStatus: CapacityGapStatus = gapTasksPerHour < 0
    ? 'deficit'
    : gapTasksPerHour === 0
      ? 'balanced'
      : 'slack';

  return {
    capacityTasksPerHour,
    gapTasksPerHour,
    requiredFte,
    fteDeficit: Math.max(0, requiredFte - availableFte),
    gapStatus,
  };
}
